import { useEffect, useState } from 'react';
import { getAllValentinePages, approveValentinePage } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface Profile {
  username: string;
}

interface ValentinePage {
  id: string;
  sender: string;
  package_type: string;
  spotify_title: string;
  spotify_artist: string;
  is_approved: boolean;
  created_at: string;
  profiles: Profile[];
}

interface AdminStats {
  total_pages: number;
  pending_approvals: number;
  approved_pages: number;
}

export default function AdminDashboard() {
  const [pages, setPages] = useState<ValentinePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    total_pages: 0,
    pending_approvals: 0,
    approved_pages: 0,
  });

  useEffect(() => {
    loadPages();
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const { data, error } = await supabase
        .from('valentine_pages')
        .select('is_approved', { count: 'exact' });

      if (error) throw error;

      const total = data.length;
      const approved = data.filter((p) => p.is_approved).length;

      setStats({
        total_pages: total,
        approved_pages: approved,
        pending_approvals: total - approved,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  async function loadPages() {
    try {
      const { data } = await getAllValentinePages();
      if (data) {
        // Only include non-sensitive fields
        const filteredPages: ValentinePage[] = data.map((page) => ({
          id: page.id,
          sender: page.sender,
          package_type: page.package_type,
          spotify_title: page.spotify_title,
          spotify_artist: page.spotify_artist,
          is_approved: page.is_approved,
          created_at: page.created_at,
          profiles: Array.isArray(page.profiles)
            ? page.profiles
            : [page.profiles].filter(Boolean),
        }));
        setPages(filteredPages);
      }
    } catch (error) {
      toast.error('Failed to load Valentine pages');
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(id: string, spotifyUrl: string) {
    try {
      await approveValentinePage(id, spotifyUrl);

      // Log admin action
      await supabase.rpc('log_admin_action', {
        action: 'approve_valentine',
        details: { valentine_id: id },
      });

      toast.success('Page approved successfully');
      loadPages();
      loadStats();
    } catch (error) {
      toast.error('Failed to approve page');
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Admin Dashboard</h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-pink-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-pink-700">Total Pages</h3>
          <p className="text-2xl font-bold text-pink-900">
            {stats.total_pages}
          </p>
        </div>
        <div className="bg-yellow-100 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800">
            Pending Approval
          </h3>
          <p className="text-2xl font-bold text-yellow-800">
            {stats.pending_approvals}
          </p>
        </div>
        <div className="bg-green-100 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800">Approved</h3>
          <p className="text-2xl font-bold text-green-800">
            {stats.approved_pages}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {pages.map((page) => (
          <div key={page.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold">From: {page.sender}</h2>
                {/* <p className="text-gray-600">
                  Created by: {page.profiles[0]?.username}
                </p> */}
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm ${
                  page.is_approved
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {page.is_approved ? 'Approved' : 'Pending'}
              </span>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <p>Package: {page.package_type}</p>
              <p>
                Song: {page.spotify_title} by {page.spotify_artist}
              </p>
            </div>
            {!page.is_approved && (
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Spotify Embed URL"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleApprove(page.id, e.currentTarget.value);
                    }
                  }}
                />
                <button
                  onClick={(event) => {
                    const input = event.currentTarget
                      .previousElementSibling as HTMLInputElement;
                    handleApprove(page.id, input.value);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
